import { useState, useEffect, useCallback } from 'react'
import { Button, Box, Typography } from '@mui/material'
import { getHeaders } from '../../utils/common'
import { DialogWrapper } from '../../components/general/DialogWrapper'
import Loading from '../../Loading'
import ErrorDisplay from '../../components/error/ErrorDisplay'

/**
 * @typedef {interface} DeleteSessionParams
 * @prop {SessionInfo} session
 * @prop {boolean} isOpen
 * @prop {callback} onDelete
 * @prop {callback} closeModal
 */
export interface DeleteSessionParams {
  session: {
    id: string
    name: string
  } | null
  isOpen: boolean
  onDelete: () => void
  closeModal: () => void
}

/**
 * Component for deleting a session, including all associated files.
 *
 * @param {Object} params - The parameters for rendering the session deletion dialog.
 * @param {Object} params.session - The session to be deleted.
 * @param {function} params.onDelete - A callback function to execute after successful deletion.
 * @param {boolean} params.isOpen - Indicates whether the session deletion dialog is open.
 * @param {function} params.closeModal - A callback function to close the session deletion dialog.
 * @returns {JSX.Element} The JSX element representing the `DeleteSession` component.
 */
export default function DeleteSession({ session, onDelete, isOpen, closeModal }: DeleteSessionParams) {
  const [deleteError, setDeleteError] = useState(null)
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false)

  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  useEffect(() => {
    setDeleteError(null)
    setIsDeleteInProgress(false)
  }, [isOpen])

  const submitDelete = useCallback(() => {
    setDeleteError(null)
    setIsDeleteInProgress(true)

    fetch(`/mqttapi/v1/session/${session?.id}`, {
      method: 'DELETE',
      headers: myHeaders,
    })
      .then(async (response) => {
        if (response.ok) {
          return response.text()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then(() => {
        onDelete()
        closeModal()
      })
      .catch((error) => setDeleteError(error))
      .finally(() => setIsDeleteInProgress(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, onDelete, setIsDeleteInProgress, closeModal])

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth="sm">
      <strong>Are you sure you want to delete {(session || {}).name || ''}?</strong>
      <Typography variant="body1">This will also delete all files belonging to the session.</Typography>
      {isDeleteInProgress ? (
        <Loading action="Deleting..." />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'right', gap: '20px' }}>
          <Button variant="contained" color="error" onClick={submitDelete}>
            Yes, DO IT
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      )}
      {deleteError !== null ? <ErrorDisplay err={deleteError} /> : null}
    </DialogWrapper>
  )
}
